import React from 'react';
import { motion } from 'framer-motion';
import { ServerIcon, WorkflowIcon, BookOpenIcon, FileTextIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { SectionNav } from '../components/SectionNav';
const CATEGORIES = [{
  Icon: ServerIcon,
  title: 'Enterprise Platforms',
  note: 'Core finance systems implemented, configured, or owned in production.',
  items: ['SAP S/4HANA (enterprise rollout lead)', 'SAP ECC general ledger', 'Oracle Financials', 'Essbase / Hyperion reporting cubes']
}, {
  Icon: WorkflowIcon,
  title: 'Automation & Analytics',
  note: 'Tooling used to remove manual effort from reporting cycles.',
  items: ['Alteryx workflow automation', 'Axiom regulatory reporting suite', 'Advanced Excel / VBA modelling', 'Robotic process automation pilots']
}, {
  Icon: BookOpenIcon,
  title: 'Accounting & Control Frameworks',
  note: 'Standards and control regimes applied in practice.',
  items: ['US GAAP technical accounting', 'IFRS group reporting', 'SOX / internal control frameworks', 'Regulation W (23A / 23B) controls']
}, {
  Icon: FileTextIcon,
  title: 'Regulatory Report Forms',
  note: 'Filings personally prepared, reviewed, or remediated.',
  items: ['FR Y-9C · FR Y-7 · FR Y-11', 'FFIEC 031 / 041 Call Reports', 'FR 2052a liquidity reporting', 'TIC (TICS / TIC B) filings']
}];
const PROGRAMS = [{
  program: 'SAP S/4HANA US Rollout',
  institution: 'Bank of Ireland',
  role: 'Program sponsor and finance lead',
  outcome: 'Customized ERP workflows so that US regulatory mandates (FFIEC, FR, TICS) are satisfied directly from the general ledger.'
}, {
  program: 'Regulatory Reporting Automation',
  institution: 'MUFG',
  role: 'Architect and advisor',
  outcome: 'Rebuilt enterprise reporting frameworks for OCC/FRB compliance and automated workflows saving 570 FTE hours.'
}, {
  program: 'Global Shared Service Migration',
  institution: 'Multiple institutions',
  role: 'Migration governance lead',
  outcome: 'Transitioned finance processes to offshore service centers with retained control ownership and documented handover evidence.'
}];
export function Systems() {
  return <>
      <PageHeader index="08" title="Technical Mastery & Systems Portfolio" intro="The platforms, automation tooling, accounting frameworks, and regulatory report forms Gerry has owned end to end — and the transformation programs built on them." data-id="element-410" data-mp="2hn1cw81q6eci" />

      <section aria-labelledby="stack" className="w-full bg-white" data-id="element-411" data-mp="23vcz80btvhtf">
        <div className="mx-auto max-w-content px-6 py-20 sm:py-24 lg:px-8" data-id="element-412" data-mp="23x7kxguxbdyk">
          <h2 id="stack" className="text-[11px] font-semibold uppercase tracking-widest text-navy-700" data-id="element-413" data-mp="1yuyuos9fqq05">
            Systems & Technical Inventory
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-px border border-slateink-300/70 bg-slateink-300/70 sm:grid-cols-2" data-id="element-414" data-mp="1jsgwjg6ctdlx">
            {CATEGORIES.map((cat, i) => <motion.article key={cat.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.45,
            delay: i % 2 * 0.08
          }} className="bg-white p-8" data-id="element-415" data-mp="1tqwo1k9amovy" data-mpi={i}>
                <span className="flex h-11 w-11 items-center justify-center border border-slateink-300 text-navy-900" data-id="element-416" data-mp="162523amar2rm">
                  <cat.Icon className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" data-id="element-417" data-mp="1az3u9nmegzn6" />
                </span>
                <h3 className="mt-6 font-serif text-xl text-navy-900" data-id="element-418" data-mp="icaqsxhmlm9h">{cat.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slateink-600" data-id="element-419" data-mp="iagfmaw5p8il">{cat.note}</p>
                <ul className="mt-6 space-y-3 border-t border-slateink-300/70 pt-6" data-id="element-420" data-mp="n99il9dthh4i">
                  {cat.items.map(item => <li key={item} className="flex gap-3 text-sm text-navy-800" data-id="element-421" data-mp="2k1qd5ewu31h0">
                      <span className="mt-2 h-1.5 w-1.5 flex-none bg-gold-500" aria-hidden="true" data-id="element-422" data-mp="2yqcqjb2whj5l" />
                      <span data-id="element-423" data-mp="2tybi608uscku">{item}</span>
                    </li>)}
                </ul>
              </motion.article>)}
          </div>
        </div>
      </section>

      <section aria-labelledby="programs" className="w-full bg-navy-950" data-id="element-424" data-mp="33owcvcleowyh">
        <div className="mx-auto max-w-content px-6 py-20 sm:py-24 lg:px-8" data-id="element-425" data-mp="3ho5tuehfrv3i">
          <h2 id="programs" className="text-[11px] font-semibold uppercase tracking-widest text-gold-400" data-id="element-426" data-mp="3cqdtqx6n12o8">
            Transformation Programs Delivered
          </h2>
          <ul className="mt-10 space-y-px bg-white/10" data-id="element-427" data-mp="1icmkhys80ktb">
            {PROGRAMS.map(p => <li key={p.program} className="grid grid-cols-1 gap-4 bg-navy-950 p-8 lg:grid-cols-12 lg:gap-8" data-id="element-428" data-mp="3tzq0f2tat1hf">
                <div className="lg:col-span-4" data-id="element-429" data-mp="2sdnx13d1c7u">
                  <p className="font-serif text-xl text-white" data-id="element-430" data-mp="3ka6gruxrh3li">{p.program}</p>
                  <p className="mt-2 text-[11px] uppercase tracking-widest text-gold-400" data-id="element-431" data-mp="3p8q685bkcxim">
                    {p.institution}
                  </p>
                </div>
                <p className="text-sm text-slateink-300 lg:col-span-3" data-id="element-432" data-mp="3b7nlpx07vv9t">{p.role}</p>
                <p className="text-[15px] leading-relaxed text-slateink-100 lg:col-span-5" data-id="element-433" data-mp="3g9mob4o4uo5p">
                  {p.outcome}
                </p>
              </li>)}
          </ul>
        </div>
      </section>

      <SectionNav currentIndex="08" data-id="element-434" data-mp="36byfjatozg4j" />
    </>;
}